import { motion } from 'framer-motion';
import { ShieldCheck, Scale, FileCheck2, Gavel } from 'lucide-react';
import { KRAFT } from '@/constants/testIds';

const CHAPTERS = [
  {
    id: 1,
    num: '01',
    title: 'Prawo Budowlane',
    icon: Scale,
    body:
      'Każda instalacja projektowana i realizowana zgodnie z Ustawą Prawo Budowlane. Odpowiedzialność zawodowa spoczywa na wykonawcy, nie na Tobie — dzięki temu odbiór techniczny obiektu nigdy nie staje się źródłem ryzyka prawnego.',
  },
  {
    id: 2,
    num: '02',
    title: 'Uprawnienia SEP D+E',
    icon: FileCheck2,
    body:
      'Prace wykonywane wyłącznie przez elektryków z aktualnymi świadectwami kwalifikacyjnymi SEP w zakresie eksploatacji (E) i dozoru (D). Każda faktura wskazuje osobę uprawnioną — dokument akceptowany przez ubezpieczyciela.',
  },
  {
    id: 3,
    num: '03',
    title: 'Normy BHP i PN-IEC 60364',
    icon: ShieldCheck,
    body:
      'Zgodność z aktualnymi normami PN-IEC dotyczącymi instalacji niskiego napięcia oraz procedurami BHP przy pracach pod napięciem. Otrzymujesz komplet protokołów pomiarowych wymaganych przy sprzedaży, wynajmie i kontroli.',
  },
  {
    id: 4,
    num: '04',
    title: 'Tylko certyfikowane komponenty',
    icon: Gavel,
    body:
      'Używamy wyłącznie osprzętu posiadającego europejskie deklaracje zgodności (CE) oraz polskie znaki bezpieczeństwa (B).',
  },
];

const cardVariants = {
  hidden: { opacity: 0, y: 40 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function LegalManifesto() {
  return (
    <section
      id="about"
      data-testid={KRAFT.manifestoRoot}
      className="relative bg-ink-950 py-24 md:py-32 overflow-hidden"
    >
      {/* Section label */}
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          className="flex items-center gap-3 mb-6"
        >
          <div className="w-8 h-px bg-volt" />
          <span className="eyebrow">Całodobowa ochrona prawna i techniczna</span>
        </motion.div>

        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="font-display uppercase text-white text-4xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight max-w-5xl"
        >
          Usuwamy awarie.<br />
          Robimy to <span className="text-volt italic font-normal">całodobowo</span>.
        </motion.h2>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="mt-8 max-w-3xl text-slate-300 text-base md:text-lg leading-relaxed"
        >
          Nie idziemy na skróty. Każda modernizacja, naprawa i nowa instalacja to u nas inwestycja w
          bezpieczeństwo i niezawodność. Wybierając nas, kupujesz spokój.
        </motion.p>

        {/* Numbered chapters — asymmetric bento */}
        <div className="mt-16 md:mt-24 grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10">
          {CHAPTERS.map((ch, i) => {
            const Icon = ch.icon;
            return (
              <motion.article
                key={ch.id}
                data-testid={KRAFT.manifestoChapter(ch.id)}
                custom={i}
                variants={cardVariants}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: '-80px' }}
                className="relative bg-ink-950 p-10 md:p-14 group hover:bg-ink-900 transition-colors duration-500"
              >
                {/* Big number */}
                <div
                  className="absolute right-6 top-6 font-display text-[110px] md:text-[150px] leading-none text-white/[0.05] group-hover:text-volt/10 transition-colors duration-500 select-none"
                  aria-hidden
                >
                  {ch.num}
                </div>
                <div className="relative">
                  <div className="flex items-center gap-4 mb-8">
                    <div className="w-12 h-12 border border-volt/50 flex items-center justify-center group-hover:bg-volt group-hover:border-volt transition-colors">
                      <Icon className="w-5 h-5 text-volt group-hover:text-ink-950 transition-colors" strokeWidth={2} />
                    </div>
                    <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-slate-400">
                      Rozdział · {ch.num}
                    </div>
                  </div>
                  <h3 className="font-display uppercase text-white text-2xl md:text-3xl tracking-tight mb-4">
                    {ch.title}
                  </h3>
                  <p className="text-slate-300 text-[15px] md:text-base leading-relaxed max-w-lg">
                    {ch.body}
                  </p>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
